import { useEffect } from 'react';
import { CheckCircle2, X } from 'lucide-react';

const AUTO_DISMISS_MS = 3000;
const SCROLL_TO_SYSTEM_MESSAGE_EVENT = 'arcanum:scroll-to-system-message';

export interface EditSyncedToastProps {
  /** systemMessageId from `ModelEditResponse` — used by the View-in-Conversation event payload. May be null when the API failed to append the system message; the link still renders but the auto-scroll on landing won't fire. */
  systemMessageId: string | null;
  onDismiss: () => void;
  /** Called after the user clicks "View in Conversation" (parent flips to `?tab=conversation`). */
  onViewInConversation: () => void;
}

/**
 * Panel-local toast — NOT a global UI primitive. Modeled visually on
 * `<ForkSuccessBanner>` (`bg-accent-dim border-l-4 border-accent/30`) so
 * the post-edit confirmation feels like a native part of the cell-detail
 * panel rather than a route-level alert. Auto-dismisses after 3s; can be
 * dismissed early via the X button or remounted (the parent toggles the
 * toast's existence via local state keyed on the latest `systemMessageId`).
 *
 * The "View in Conversation" link both navigates to `?tab=conversation`
 * (the parent's `onViewInConversation` handler) AND dispatches the
 * `arcanum:scroll-to-system-message` window event so Phase 4 Session 4's
 * `<ChatThread>` grouping can scroll the matching system-message group
 * into view + expand it. The dispatch is a no-op when `systemMessageId`
 * is null (e.g. idempotent replay or the API's append-isolation branch).
 */
export function EditSyncedToast({
  systemMessageId,
  onDismiss,
  onViewInConversation,
}: EditSyncedToastProps) {
  useEffect(() => {
    const timer = setTimeout(onDismiss, AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  const handleClickLink = () => {
    if (systemMessageId !== null && typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent(SCROLL_TO_SYSTEM_MESSAGE_EVENT, {
          detail: { systemMessageId },
        }),
      );
    }
    onViewInConversation();
  };

  return (
    <div
      className="flex items-start gap-2 bg-accent-dim border-l-4 border-accent/30 rounded-r-md p-3"
      role="status"
      data-testid="edit-synced-toast"
    >
      <CheckCircle2 size={14} className="text-accent shrink-0 mt-0.5" />
      <div className="flex-1 text-xs font-mono leading-relaxed text-text">
        <div>Edit synced to Conversation.</div>
        <button
          type="button"
          onClick={handleClickLink}
          className="text-accent hover:text-text-bright underline-offset-2 hover:underline cursor-pointer bg-transparent border-none p-0 mt-0.5 text-[11px]"
          data-testid="edit-synced-toast-cta"
        >
          View in Conversation
        </button>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="text-text-dim hover:text-text bg-transparent border-none p-0.5 cursor-pointer"
        data-testid="edit-synced-toast-dismiss"
      >
        <X size={12} />
      </button>
    </div>
  );
}
